// Die Dateien am Gerät – Fotos und Sprachmemos (seit 19.10.2026, Speicher
// Version 2). Sie liegen als ArrayBuffer in der Tabelle „dateien“, benannt
// nach ihrer Prüfsumme: einmal geschrieben, nie überschrieben, und weg,
// sobald kein Foto und kein Memo sie mehr braucht. Zum Anzeigen, Abspielen
// und Senden entsteht daraus jedes Mal ein frischer Blob im Arbeitsspeicher.
//
// Warum nicht mehr als Blob am Datensatz: Safari macht einen Blob aus
// IndexedDB unlesbar, wenn er nach einem Neustart der App gelesen und
// wieder gespeichert wird (WebKit-Fehler 235687, „WebKitBlobResource error
// 1“, seit iOS 13 offen) – und jede Änderung an einem Foto (Zeichnung,
// „hochgeladen“, „abgeschickt“) speichert den ganzen Datensatz neu. So zeigte
// am iPhone ein Foto, das offline entstand und nach einem Neustart gesendet
// wurde, nur noch ein Fragezeichen; ein Sprachmemo ließ sich nach dem Senden
// nicht mehr abspielen. Ein ArrayBuffer liegt in IndexedDB als Teil des
// Datensatzes, nicht als eigene Datei – das betrifft ihn nicht. Und weil die
// Bytes in einer eigenen Tabelle stehen, bleiben die Datensätze klein: Die
// Seiten, die sich bei jeder Änderung neu lesen, lesen keine Fotos mit.
//
// Seit E2, Schritt 4: Ist eine Besichtigung im Büro, kommen ihre Dateien
// vom Gerät (`dateienFreigeben`) – ein verlorenes Handy trägt keine
// Hausfotos (Gernot, 19.10.2026). Zum Nachsehen holt `dateiZumAnsehen` sie
// dann vom Server, nur in den Arbeitsspeicher. Was nachgereicht wird (seit
// Schritt 5), bleibt am Gerät, bis auch sein Nachtrag im Büro ist.

import { PRUEFSUMME_MUSTER, XLSX } from "@happy-energy/kern";
import { ApiFehler, dateiHolen, OhneVerbindung } from "@happy-energy/oberflaeche";
import { speicher } from "./speicher.ts";
import { pruefsummeBerechnen } from "./foto.ts";

/** Der Typ, falls ein Blob keinen trägt. */
const OHNE_TYP = "application/octet-stream";

/**
 * Legt eine Datei ab, wenn es sie noch nicht gibt – nie überschreiben (gleiche
 * Prüfsumme heißt gleicher Inhalt). Muss in einer Transaktion laufen, die
 * `speicher.dateien` mitnimmt, zusammen mit dem Datensatz, der sie braucht.
 */
export async function dateiAblegen(pruefsumme: string, daten: ArrayBuffer, typ: string): Promise<void> {
  if ((await speicher.dateien.where("pruefsumme").equals(pruefsumme).count()) > 0) return;
  await speicher.dateien.add({ pruefsumme, daten, typ: typ || OHNE_TYP });
}

/**
 * Braucht noch ein Foto oder Memo am Gerät diese Datei? Sonst kommt sie weg.
 * In einer Transaktion mit `speicher.dateien`, `speicher.fotos` und
 * `speicher.memos`, nachdem der Datensatz gelöscht ist.
 */
export async function dateiWegWennUnbenutzt(pruefsumme: string | null): Promise<void> {
  if (pruefsumme === null) return;
  const fotos = await speicher.fotos.where("pruefsumme").equals(pruefsumme).count();
  const memos = await speicher.memos.filter((m) => m.pruefsumme === pruefsumme).count();
  if (fotos + memos === 0) await speicher.dateien.delete(pruefsumme);
}

/** Ein alter Blob (vor Version 2) als frische Kopie – null, wenn Safari ihn nicht mehr lesen kann. */
async function altLesen(datei: Blob, typ: string | undefined): Promise<Blob | null> {
  try {
    return new Blob([await datei.arrayBuffer()], { type: typ ?? datei.type });
  } catch {
    return null;
  }
}

/**
 * Die Datei mit dieser Prüfsumme als frischer Blob im Arbeitsspeicher – zum
 * Anzeigen, Abspielen und Senden. `typ`: ein anderer Typ für die Hülle (etwa
 * „audio/mp4“ ohne Codec-Zusatz). Null: Sie liegt nicht (mehr) am Gerät
 * oder ist nicht mehr lesbar.
 */
export async function dateiLesen(pruefsumme: string, typ?: string): Promise<Blob | null> {
  const d = await speicher.dateien.get(pruefsumme);
  if (d) return new Blob([d.daten], { type: typ ?? d.typ });
  // Noch nicht umgezogen (vor Version 2): der Blob am Foto oder Memo.
  const f = await speicher.fotos
    .where("pruefsumme")
    .equals(pruefsumme)
    .filter((x) => x.datei instanceof Blob)
    .first();
  if (f?.datei) return altLesen(f.datei, typ);
  const m = await speicher.memos.filter((x) => x.pruefsumme === pruefsumme && x.datei instanceof Blob).first();
  if (m?.datei) return altLesen(m.datei, typ);
  return null;
}

/**
 * Die Datei, wenn sie am Gerät liegt: ein Foto oder Memo (`dateiLesen`)
 * oder das K2-PDF – das liegt als ArrayBuffer an seiner Dachplanung, seit
 * E3c (S5) ebenso der Excel-Report von K2. Sie sind je Gerät wenige: Sie
 * werden ohne eigenen Index gesucht. Null: nicht (mehr) am Gerät.
 */
export async function dateiAmGeraet(pruefsumme: string, typ?: string): Promise<Blob | null> {
  const datei = await dateiLesen(pruefsumme, typ);
  if (datei) return datei;
  const k = await speicher.k2.filter((x) => x.pruefsumme === pruefsumme && x.pdf !== null).first();
  if (k?.pdf) return new Blob([k.pdf], { type: typ ?? "application/pdf" });
  const e = await speicher.k2.filter((x) => x.excel?.pruefsumme === pruefsumme && x.excel.daten !== null).first();
  return e?.excel?.daten ? new Blob([e.excel.daten], { type: typ ?? XLSX }) : null;
}

/** Liegt die Datei (lesbar oder nicht) noch am Gerät? Ohne sie zu lesen. */
export async function dateiDa(pruefsumme: string): Promise<boolean> {
  return (await speicher.dateien.where("pruefsumme").equals(pruefsumme).count()) > 0;
}

/**
 * Beim Start, einmal: Blobs aus der Zeit vor Version 2 in „dateien“
 * umziehen. Der Blob wird nur gelesen (das ist unbedenklich), die Bytes
 * landen als ArrayBuffer in der neuen Tabelle, und am Datensatz wird das
 * Feld null – so speichert keine spätere Änderung den alten Blob mehr neu.
 * Ist ein Blob schon unlesbar, bleibt er, wie er ist (die Seite sagt dann,
 * dass das Foto am Gerät nicht lesbar ist). Gibt zurück, wie viele umzogen.
 */
export async function dateienUmziehen(): Promise<number> {
  let umgezogen = 0;
  const alteFotos = await speicher.fotos.filter((x) => x.datei instanceof Blob).toArray();
  const alteMemos = await speicher.memos.filter((x) => x.datei instanceof Blob && x.pruefsumme !== null).toArray();
  const alle = [
    ...alteFotos.map((f) => ({ tabelle: "fotos" as const, id: f.id, pruefsumme: f.pruefsumme, datei: f.datei! })),
    ...alteMemos.map((m) => ({ tabelle: "memos" as const, id: m.id, pruefsumme: m.pruefsumme!, datei: m.datei! })),
  ];
  for (const alt of alle) {
    let daten: ArrayBuffer;
    try {
      daten = await alt.datei.arrayBuffer();
    } catch {
      continue;
    }
    // Nur, was wirklich die Datei mit dieser Prüfsumme ist.
    if ((await pruefsummeBerechnen(daten)) !== alt.pruefsumme) continue;
    await speicher.transaction("rw", speicher.dateien, speicher.fotos, speicher.memos, async () => {
      const tabelle = alt.tabelle === "fotos" ? speicher.fotos : speicher.memos;
      const jetzt = await tabelle.get(alt.id);
      if (!(jetzt?.datei instanceof Blob)) return;
      await dateiAblegen(alt.pruefsumme, daten, alt.datei.type);
      await tabelle.update(alt.id, { datei: null });
      umgezogen += 1;
    });
  }
  return umgezogen;
}

// --- Nach „Ist im Büro“: die Dateien freigeben (seit E2, Schritt 4) -------------------------

/**
 * Der Server hat bestätigt, dass alles da ist: Die Bytes der Fotos und
 * Memos dieser Besichtigung kommen vom Gerät – außer eine Datei derselben
 * Prüfsumme wartet noch im Postausgang, oder ein Foto oder Memo, dessen
 * Sendung noch nicht im Büro ist, braucht sie (eine andere Besichtigung
 * oder ein Nachtrag, der noch läuft – seit Schritt 5: Was gerade
 * nachgereicht wird, bleibt am Gerät, bis der Server den Nachtrag
 * bestätigt). Das K2-PDF und (seit E3c, S5) der Excel-Report ebenso, sobald
 * ihre Datei nicht mehr wartet (sie dürfen nach dem Senden noch kommen). Die Datensätze bleiben als Kopie zum
 * Nachsehen: Foto mit Prüfsumme, Maßen und Zeichnung, Memo mit Dauer,
 * Dachplanung mit Prüfsumme und Größe. Nur für eine Besichtigung, die im
 * Büro ist – sonst nichts.
 */
export async function dateienFreigeben(aufnahmeId: string): Promise<void> {
  const tabellen = [speicher.aufnahmen, speicher.nachtraege, speicher.fotos, speicher.memos, speicher.k2, speicher.dateien, speicher.postausgang];
  await speicher.transaction("rw", tabellen, async () => {
    const a = await speicher.aufnahmen.get(aufnahmeId);
    if (a?.stand !== "im_buero") return;
    const wartet = new Set((await speicher.postausgang.filter((e) => e.art === "datei").toArray()).map((e) => e.kennung));
    const fotos = await speicher.fotos.where("aufnahmeId").equals(aufnahmeId).toArray();
    const memos = await speicher.memos.where("aufnahmeId").equals(aufnahmeId).toArray();
    const summen = new Set([...fotos.map((f) => f.pruefsumme), ...memos.flatMap((m) => (m.pruefsumme !== null ? [m.pruefsumme] : []))]);
    for (const summe of summen) {
      if (wartet.has(summe) || (await nochGebraucht(summe))) continue;
      await speicher.dateien.delete(summe);
      // Ein alter Blob am Datensatz (vor Version 2, nie umgezogen) geht mit.
      await speicher.fotos
        .where("pruefsumme")
        .equals(summe)
        .filter((f) => f.aufnahmeId === aufnahmeId && f.datei instanceof Blob)
        .modify({ datei: null });
      await speicher.memos.filter((m) => m.aufnahmeId === aufnahmeId && m.pruefsumme === summe && m.datei instanceof Blob).modify({ datei: null });
    }
    for (const k of await speicher.k2.where("aufnahmeId").equals(aufnahmeId).toArray()) {
      if (k.pdf !== null && k.pruefsumme !== null && !wartet.has(k.pruefsumme)) await speicher.k2.update(k.id, { pdf: null });
      if (k.excel?.daten && !wartet.has(k.excel.pruefsumme)) await speicher.k2.update(k.id, { excel: { ...k.excel, daten: null } });
    }
  });
}

/**
 * Die Besichtigungen, an denen noch Bytes am Gerät hängen: ein Foto oder Memo,
 * dessen Datei in „dateien“ liegt (oder noch als alter Blob am Datensatz),
 * ein K2-PDF oder ein Excel-Report. Nur sie muss der Abgleich der Startseite freigeben – alle
 * anderen sind längst frei (O55, aus der Code-Prüfung E2, Befund 4: vorher
 * fasste jeder Abgleich jede Kopie mit einer eigenen Transaktion an, mit
 * hunderten Kopien wurde die Startseite träge). Ein Durchgang je Tabelle.
 */
export async function aufnahmenMitDateien(): Promise<Set<string>> {
  const summen = new Set(await speicher.dateien.toCollection().primaryKeys());
  const ids = new Set<string>();
  await speicher.fotos.each((f) => {
    if (summen.has(f.pruefsumme) || f.datei instanceof Blob) ids.add(f.aufnahmeId);
  });
  await speicher.memos.each((m) => {
    if ((m.pruefsumme !== null && summen.has(m.pruefsumme)) || m.datei instanceof Blob) ids.add(m.aufnahmeId);
  });
  await speicher.k2.each((k) => {
    if (k.pdf !== null || (k.excel?.daten ?? null) !== null) ids.add(k.aufnahmeId);
  });
  return ids;
}

/**
 * Braucht ein Foto oder Memo diese Datei, dessen Sendung noch nicht im Büro
 * ist? Die erste Sendung ist es, wenn die Besichtigung im Büro ist; ein
 * Nachtrag, wenn auch er bestätigt ist. Ein Nachtrag, den das Gerät nicht
 * kennt (die Kopie kam vom Server, einsicht.ts), ist dort – seine Dateien
 * liegen ohnehin nicht hier.
 */
async function nochGebraucht(pruefsumme: string): Promise<boolean> {
  const sendungen = [
    ...(await speicher.fotos.where("pruefsumme").equals(pruefsumme).toArray()),
    ...(await speicher.memos.filter((m) => m.pruefsumme === pruefsumme).toArray()),
  ];
  for (const x of sendungen) {
    if ((await speicher.aufnahmen.get(x.aufnahmeId))?.stand !== "im_buero") return true;
    const nachtrag = x.nachtragId ? await speicher.nachtraege.get(x.nachtragId) : undefined;
    if (nachtrag && nachtrag.stand !== "im_buero") return true;
  }
  return false;
}

// --- Zum Ansehen: am Gerät oder aus dem Büro (seit E2, Schritt 4) --------------------------
//
// Nach dem Senden liegen Fotos, Memos und das K2-PDF nicht mehr am Gerät.
// Zum Nachsehen holt die App sie vom Server (GET /api/doku/datei/<prüfsumme>)
// – nur in den Arbeitsspeicher, nie in IndexedDB. Damit das Blättern
// zwischen Station und Foto nicht jedes Mal neu lädt, merkt sie sich die
// zuletzt geholten bis etwa 40 MB; schließt die App, sind sie weg.

/** So viel aus dem Büro Geholtes bleibt höchstens im Arbeitsspeicher. */
const GEHOLT_HOECHSTENS = 40 * 1024 * 1024;
/** Zuletzt benutzt steht hinten. */
const geholt = new Map<string, Blob>();
/** Was gerade geholt wird – zwei Kacheln mit demselben Foto holen es einmal. */
const unterwegs = new Map<string, Promise<Blob | null>>();

function merken(pruefsumme: string, blob: Blob): void {
  geholt.delete(pruefsumme);
  geholt.set(pruefsumme, blob);
  let summe = 0;
  for (const b of geholt.values()) summe += b.size;
  for (const [p, b] of geholt) {
    if (summe <= GEHOLT_HOECHSTENS) break;
    geholt.delete(p);
    summe -= b.size;
  }
}

/** Den Arbeitsspeicher für Geholtes leeren – für Tests. */
export function geholtVergessen(): void {
  geholt.clear();
}

function mitTyp(blob: Blob, typ: string | undefined): Blob {
  return typ && blob.type !== typ ? new Blob([blob], { type: typ }) : blob;
}

async function vomServer(pruefsumme: string): Promise<Blob | null> {
  let blob: Blob;
  try {
    blob = await dateiHolen(`/api/doku/datei/${pruefsumme}`);
  } catch (fehler) {
    if (fehler instanceof ApiFehler && fehler.status === 404) return null;
    throw fehler;
  }
  // Nur, was wirklich die Datei mit dieser Prüfsumme ist – lieber nichts zeigen als das Falsche.
  if ((await pruefsummeBerechnen(blob)) !== pruefsumme) throw new Error("Die Datei aus dem Büro passt nicht zu ihrer Prüfsumme.");
  merken(pruefsumme, blob);
  return blob;
}

/**
 * Eine Datei zum Ansehen, Abspielen oder Öffnen: erst am Gerät
 * (`dateiAmGeraet`), sonst vom Server – als Blob nur im Arbeitsspeicher.
 * `typ`: ein anderer Typ für die Hülle (wie bei `dateiLesen`). `beimHolen`
 * meldet, dass es aus dem Büro kommt (das dauert). Null: weder am Gerät noch
 * am Server. Ohne Netz: `OhneVerbindung`; andere Fehler wie bei `dateiHolen`.
 */
export async function dateiZumAnsehen(pruefsumme: string, typ?: string, beimHolen?: () => void): Promise<Blob | null> {
  const hier = await dateiAmGeraet(pruefsumme, typ);
  if (hier) return hier;
  const gemerkt = geholt.get(pruefsumme);
  if (gemerkt) {
    merken(pruefsumme, gemerkt);
    return mitTyp(gemerkt, typ);
  }
  if (!PRUEFSUMME_MUSTER.test(pruefsumme)) return null;
  beimHolen?.();
  let holen = unterwegs.get(pruefsumme);
  if (!holen) {
    holen = vomServer(pruefsumme).finally(() => unterwegs.delete(pruefsumme));
    unterwegs.set(pruefsumme, holen);
  }
  const blob = await holen;
  return blob ? mitTyp(blob, typ) : null;
}

/** Wie es um eine Datei zum Ansehen steht – für Foto, Memo und PDF. */
export type DateiStand =
  /** Wird am Gerät gelesen. */
  | { art: "laedt" }
  /** Liegt nicht am Gerät – wird aus dem Büro geholt. */
  | { art: "holt" }
  | { art: "da"; url: string }
  /** Liegt nicht am Gerät, und gerade ist kein Empfang (oder der Server antwortet nicht): kommt mit dem Netz. */
  | { art: "ohne-netz" }
  /** Weder am Gerät lesbar noch vom Server zu bekommen. */
  | { art: "fehlt" };

/**
 * Was ein Fehler beim Holen heißt: „ohne-netz“, wenn es am Empfang oder am
 * Server liegt (dann später noch einmal) – sonst „fehlt“.
 */
export function holenFehlgeschlagen(fehler: unknown): Extract<DateiStand, { art: "ohne-netz" | "fehlt" }> {
  if (fehler instanceof OhneVerbindung) return { art: "ohne-netz" };
  if (fehler instanceof ApiFehler && (fehler.keinJson || fehler.status >= 500 || [401, 403, 408, 429].includes(fehler.status))) {
    return { art: "ohne-netz" };
  }
  return { art: "fehlt" };
}
